import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, STATUS_ORDER, formatINR, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Search, Info, Upload, ArrowRight, Building2, FileSpreadsheet, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import ProductPicker from "@/components/ProductPicker";
import DateRangeFilter from "@/components/DateRangeFilter";

function DistributorPicker({ open, onOpenChange, onPick }) {
  const [distributors, setDistributors] = useState([]);
  const [state, setState] = useState("");
  const [district, setDistrict] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/distributors").then((r) => setDistributors(r.data));
    setState(""); setDistrict(""); setQ("");
  }, [open]);

  const states = useMemo(() => Array.from(new Set(distributors.map((d) => d.state).filter(Boolean))).sort(), [distributors]);
  const districts = useMemo(() => Array.from(new Set(distributors.filter((d) => !state || d.state === state).map((d) => d.district).filter(Boolean))).sort(), [distributors, state]);
  const list = useMemo(() => distributors
    .filter((d) => (!state || d.state === state) && (!district || d.district === district))
    .filter((d) => !q || d.name.toLowerCase().includes(q.toLowerCase())), [distributors, state, district, q]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading text-xl">New PFI · Select Distributor</DialogTitle>
          <p className="text-sm text-slate-500 mt-1">Pick the distributor first. Items and pricing will open on the next step.</p>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>State</Label>
            <Select value={state} onValueChange={(v) => { setState(v); setDistrict(""); }}>
              <SelectTrigger data-testid="dist-state"><SelectValue placeholder="Any state" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All states</SelectItem>
                {states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>District</Label>
            <Select value={district} onValueChange={(v) => setDistrict(v === "__all" ? "" : v)} disabled={!state || state === "__all"}>
              <SelectTrigger data-testid="dist-district"><SelectValue placeholder="Any district" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All districts</SelectItem>
                {districts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Search</Label>
            <Input placeholder="Search distributor name…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="dist-search" />
          </div>
        </div>
        <div className="max-h-72 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
          {list.length === 0 ? <div className="p-6 text-sm text-slate-500 text-center">No distributors match.</div> :
            list.map((d) => (
              <button key={d.id} className="w-full p-3 text-left hover:bg-slate-50 flex items-center gap-3" onClick={() => { onPick(d); onOpenChange(false); }} data-testid={`dist-pick-${d.id}`}>
                <Building2 className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{d.name}</div>
                  <div className="text-xs text-slate-500 truncate">{[d.district, d.state, d.region].filter(Boolean).join(" · ") || "—"} · {d.payment_terms || "no terms"}</div>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400" />
              </button>
            ))
          }
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OrderEntryDialog({ open, onOpenChange, distributor, order, onCreated }) {
  const [products, setProducts] = useState([]);
  const [rateType, setRateType] = useState("distributor");
  const [cashDisc, setCashDisc] = useState(0);
  const [gstPct, setGstPct] = useState(18);
  const [remarks, setRemarks] = useState("");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [autoFocusIdx, setAutoFocusIdx] = useState(-1);    // row index whose picker should auto-open
  const [recentSkus, setRecentSkus] = useState(() => {
    try { return JSON.parse(localStorage.getItem("oms_recent_skus") || "[]"); } catch { return []; }
  });
  const editing = !!order?.id;

  function pushRecent(sku) {
    if (!sku) return;
    setRecentSkus((prev) => {
      const norm = sku.toUpperCase();
      const next = [norm, ...prev.filter((s) => s.toUpperCase() !== norm)].slice(0, 15);
      try { localStorage.setItem("oms_recent_skus", JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }

  useEffect(() => {
    if (!open || !distributor) return;
    if (editing) {
      setRateType(order.rate_type || "distributor");
      setCashDisc(order.cash_disc_pct || 0);
      setGstPct(order.gst_pct || 18);
      setRemarks(order.remarks || "");
      setPaymentTerms(order.payment_terms || "");
      setItems((order.items || []).filter((it) => !it.is_free_item).map((it) => ({
        product_id: it.product_id || "", sku: it.sku || "", name: it.name || "", category: it.category || "",
        quantity: Number(it.quantity || 0), free_quantity: Number(it.free_quantity || 0),
        rate: Number(it.rate || 0), unit_weight: Number(it.unit_weight || 0),
        scheme_text: it.scheme_text || "", scheme_struct: it.scheme_struct || {},
      })));
    } else {
      setRateType("distributor"); setCashDisc(0); setGstPct(18); setRemarks("");
      setPaymentTerms(distributor.payment_terms || "");
      setItems([{ product_id: "", sku: "", name: "", quantity: 1, free_quantity: 0, rate: 0, unit_weight: 0, scheme_text: "", scheme_struct: {} }]);
      setAutoFocusIdx(0);
    }
    // Safe fetch: never crash if products endpoint fails
    const fetchProducts = async () => {
      try {
        if (distributor.region_id) {
          const r = await api.get("/products", { params: { region_id: distributor.region_id } });
          if (r.data && r.data.length > 0) { setProducts(r.data); return; }
          const r2 = await api.get("/products");
          setProducts(r2.data || []);
        } else {
          const r = await api.get("/products");
          setProducts(r.data || []);
        }
      } catch (err) {
        console.error("Product load failed", err);
        setProducts([]);
        toast.error("Could not load product list — please retry");
      }
    };
    fetchProducts();
  }, [open, distributor, order, editing]);

  function addItem() {
    const nextIdx = items.length;
    setItems([...items, { product_id: "", sku: "", name: "", quantity: 1, free_quantity: 0, rate: 0, unit_weight: 0, scheme_text: "", scheme_struct: {} }]);
    setAutoFocusIdx(nextIdx);
  }
  function focusQty(i) {
    // Defer: wait for the popover to close + React commit
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="pfi-qty-${i}"]`);
      if (el) { el.focus(); el.select?.(); }
    }, 50);
  }
  // Deep-equality helper for scheme_struct + rate_type comparison
  function sameSchemeContext(a, b) {
    try { return JSON.stringify(a?.scheme_struct || {}) === JSON.stringify(b?.scheme_struct || {}); } catch { return false; }
  }

  function selectProduct(i, p) {
    // Duplicate handling — merge only if same SKU, same rate_type, same scheme context
    const dupIdx = items.findIndex((it, idx) => idx !== i && it.product_id === p.id);
    if (dupIdx !== -1 && sameSchemeContext(items[dupIdx], { scheme_struct: p.scheme_struct })) {
      const incoming = Number(items[i]?.quantity || 0) || 1;
      const beforeSnapshot = items.slice(); // full restore support for "Keep separate"
      setItems((prev) => {
        const next = [...prev];
        next[dupIdx] = { ...next[dupIdx], quantity: Number(next[dupIdx].quantity || 0) + incoming };
        next.splice(i, 1);
        return next;
      });
      setAutoFocusIdx(-1);
      const mergedFocusIdx = dupIdx > i ? dupIdx - 1 : dupIdx;
      focusQty(mergedFocusIdx);
      toast.success(`Merged with existing ${p.sku} (row ${dupIdx + 1}) · +${incoming}`, {
        action: {
          label: "Keep separate",
          onClick: () => { setItems(beforeSnapshot); setAutoFocusIdx(-1); focusQty(i); },
        },
      });
      pushRecent(p.sku);
      return;
    }
    updateItem(i, "product_id", p.id);
    pushRecent(p.sku);
    setAutoFocusIdx(-1);
    focusQty(i);
  }

  function handleQtyChange(i, raw) {
    if (raw === "" || raw === "-") { updateItem(i, "quantity", raw); return; }
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    if (n < 0) { toast.error("Quantity cannot be negative"); updateItem(i, "quantity", 0); return; }
    updateItem(i, "quantity", Math.floor(n));
  }
  function handleQtyBlur(i) {
    const it = items[i];
    // If user left qty=0 (and row has a product), auto-remove it
    if (it && it.product_id && Number(it.quantity || 0) === 0 && items.length > 1) {
      removeItem(i);
    }
  }
  function handleQtyKeyDown(e, i) {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const it = items[i];
    const q = Number(it.quantity || 0);
    if (!it.product_id) { toast.error("Pick a product first"); return; }
    if (q < 1) { toast.error("Enter quantity (whole cases only)"); return; }
    if (!Number.isInteger(q)) { toast.error("Whole cases only — no decimals"); return; }
    if (i === items.length - 1) {
      addItem();
    } else {
      focusQty(i + 1);
    }
  }
  function updateItem(i, k, v) {
    const c = [...items]; c[i] = { ...c[i], [k]: v };
    if (k === "product_id") {
      const p = products.find((pp) => pp.id === v);
      if (p) { c[i].sku = p.sku; c[i].name = p.name; c[i].category = p.category; c[i].rate = rateType === "super_stockist" ? p.super_stockist_price : p.distributor_price; c[i].unit_weight = p.case_weight_kg || 0; c[i].scheme_text = p.scheme_text || ""; c[i].scheme_struct = p.scheme_struct || {}; }
    }
    setItems(c);
  }
  function removeItem(i) { setItems(items.filter((_, idx) => idx !== i)); }
  useEffect(() => { setItems((its) => its.map((it) => { if (!it.product_id) return it; const p = products.find((pp) => pp.id === it.product_id); if (!p) return it; return { ...it, rate: rateType === "super_stockist" ? p.super_stockist_price : p.distributor_price }; })); /* eslint-disable-next-line */ }, [rateType, products]);

  function autoFree(it) { const s = it.scheme_struct || {}; const q = Number(it.quantity || 0); let f = 0; if (s.buy_qty && s.same_free) f += Math.floor(q / Number(s.buy_qty)) * Number(s.same_free); if (s.per_unit_same_free) f += q * Number(s.per_unit_same_free); return f; }
  function effFree(it) { return Number(it.free_quantity || 0) > 0 ? Number(it.free_quantity) : autoFree(it); }
  const totals = useMemo(() => {
    const gross = items.reduce((a, i) => a + Number(i.quantity || 0) * Number(i.rate || 0), 0);
    const disc = gross * Number(cashDisc || 0) / 100; const tax = gross - disc;
    const gst = tax * Number(gstPct || 0) / 100; const final = tax + gst;
    const tq = items.reduce((a, i) => a + Number(i.quantity || 0), 0);
    const tf = items.reduce((a, i) => a + effFree(i), 0);
    const tw = items.reduce((a, i) => a + (Number(i.quantity || 0) + effFree(i)) * Number(i.unit_weight || 0), 0);
    return { gross, disc, tax, gst, final, tq, tf, tw };
  }, [items, cashDisc, gstPct]);
  function recommender(it) { const s = it.scheme_struct || {}; const q = Number(it.quantity || 0); if (s.buy_qty && s.same_free && q > 0 && q % s.buy_qty !== 0) return `Add ${s.buy_qty - (q % s.buy_qty)} more for ${s.same_free} free`; if (s.free_articles?.length) for (const a of s.free_articles) { const t = Number(a.trigger_qty || 1); if (t > 1 && q > 0 && q % t !== 0) return `Add ${t - (q % t)} more for ${a.qty} ${a.name}`; } return null; }

  async function submit() {
    if (!distributor?.id) return toast.error("No distributor");
    if (items.length === 0) return toast.error("Add at least one line item");
    setSaving(true);
    try {
      const payload = {
        distributor_id: distributor.id, region_id: distributor.region_id, rate_type: rateType,
        payment_terms: paymentTerms, remarks, cash_disc_pct: Number(cashDisc), gst_pct: Number(gstPct),
        items: items.map((i) => ({ product_id: i.product_id || null, sku: i.sku, name: i.name, category: i.category || "", quantity: Number(i.quantity), free_quantity: Number(i.free_quantity || 0), rate: Number(i.rate), unit_weight: Number(i.unit_weight), scheme_text: i.scheme_text || "", scheme_struct: i.scheme_struct || {} })),
      };
      const { data } = editing
        ? await api.patch(`/orders/${order.id}`, payload)
        : await api.post("/orders", payload);
      toast.success(`Order ${data.pfi_number} ${editing ? "updated" : "created"}`); onOpenChange(false); onCreated(data);
    } catch (e) { toast.error(e?.response?.data?.detail || `${editing ? "Update" : "Create"} failed`); }
    finally { setSaving(false); }
  }

  if (!distributor) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[94vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="heading text-xl">{editing ? `Edit Draft · ${order.pfi_number}` : `New PFI · ${distributor.name}`}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500 mt-1">{[distributor.district, distributor.state, distributor.region].filter(Boolean).join(" · ")}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <Label>Rate Type</Label>
            <Select value={rateType} onValueChange={setRateType}>
              <SelectTrigger data-testid="rate-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="distributor">Distributor</SelectItem>
                <SelectItem value="super_stockist">Super Stockist</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Payment Terms</Label><Input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} /></div>
          <div><Label>Credit Discount %</Label><Input type="number" value={cashDisc} onChange={(e) => setCashDisc(e.target.value)} data-testid="credit-disc-pct" /></div>
          <div><Label>GST %</Label><Input type="number" value={gstPct} onChange={(e) => setGstPct(e.target.value)} /></div>
          <div className="md:col-span-4"><Label>Remarks</Label><Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} /></div>
        </div>

        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[10px] uppercase tracking-wide">
              <tr><th className="px-2 py-1.5 text-left">Product</th><th className="px-2 py-1.5 w-20 text-right">Qty</th><th className="px-2 py-1.5 w-16 text-right">Free</th><th className="px-2 py-1.5 w-24 text-right">Rate</th><th className="px-2 py-1.5 w-28 text-right">Amount</th><th className="px-2 py-1.5 w-8"></th></tr>
            </thead>
            <tbody>
              {items.map((it, i) => {
                const auto = autoFree(it); const rec = recommender(it);
                const effectiveFree = auto;
                return (
                  <React.Fragment key={i}>
                    <tr className="border-t border-slate-100">
                      <td className="p-1.5">
                        <ProductPicker
                          products={products}
                          value={it.product_id}
                          rateKey={rateType === "super_stockist" ? "super_stockist_price" : "distributor_price"}
                          onSelect={(p) => selectProduct(i, p)}
                          testId={`pfi-product-${i}`}
                          autoOpen={autoFocusIdx === i}
                          recentSkus={recentSkus}
                        />
                        {it.scheme_text && (
                          <div className="text-[10.5px] mt-0.5 leading-tight flex items-center gap-1.5 flex-wrap">
                            <span className="text-emerald-700/80">{it.scheme_text}</span>
                            {effectiveFree > 0 && Number(it.quantity || 0) > 0 && (
                              <span className="text-slate-500">→ Applied: <b className="text-slate-900">{Number(it.quantity) + effectiveFree}</b> <span className="text-slate-500">({Number(it.quantity)}+{effectiveFree})</span></span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-1.5"><Input className="h-8 text-right text-[12.5px]" type="number" min="0" step="1" value={it.quantity} onChange={(e) => handleQtyChange(i, e.target.value)} onBlur={() => handleQtyBlur(i)} onKeyDown={(e) => handleQtyKeyDown(e, i)} data-testid={`pfi-qty-${i}`} /></td>
                      <td className="p-1.5 text-right text-[12.5px]" data-testid={`pfi-free-${i}`}>{effectiveFree > 0 ? <span className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded bg-emerald-50 text-emerald-700 border border-emerald-200 font-semibold">+{effectiveFree}</span> : <span className="text-slate-400">0</span>}</td>
                      <td className="p-1.5 text-right text-slate-700 text-[12.5px]" data-testid={`pfi-rate-${i}`}>{it.rate ? formatINR(it.rate) : <span className="text-slate-400">—</span>}</td>
                      <td className="p-1.5 text-right font-medium text-[12.5px]" data-testid={`pfi-amount-${i}`}>{formatINR(Number(it.quantity || 0) * Number(it.rate || 0))}</td>
                      <td className="p-1.5"><Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeItem(i)}><Trash2 className="h-3.5 w-3.5 text-slate-500" /></Button></td>
                    </tr>
                    {rec && <tr className="bg-amber-50"><td colSpan={6} className="px-3 py-1.5 text-xs text-amber-800 flex items-center gap-1"><Info className="h-3 w-3" /> {rec}</td></tr>}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          <div className="p-2.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn"><Plus className="h-3 w-3 mr-1" /> Add Line Item <kbd className="ml-2 text-[10px] text-slate-500 font-mono bg-white border border-slate-200 px-1 py-[1px] rounded">Enter</kbd></Button>
            <div className="flex gap-4 text-xs text-slate-600 flex-wrap items-center">
              <div>Qty <b className="text-slate-900">{totals.tq}</b></div>
              <div>Free <b className="text-emerald-700">+{totals.tf.toFixed(0)}</b></div>
              <div data-testid="totals-weight">Wt <b className="text-slate-900">{totals.tw.toFixed(2)} kg</b></div>
              <div className="h-3 w-px bg-slate-300" />
              <div>Gross <b className="text-slate-900">{formatINR(totals.gross)}</b></div>
              <div>Credit Disc <b className="text-slate-900">-{formatINR(totals.disc)}</b></div>
              <div>Taxable <b className="text-slate-900">{formatINR(totals.tax)}</b></div>
              <div>GST <b className="text-slate-900">{formatINR(totals.gst)}</b></div>
              <div className="text-sm">Final <b className="text-slate-900">{formatINR(totals.final)}</b></div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="order-save" className="bg-slate-900 hover:bg-slate-800 text-white">{saving ? "Saving…" : (editing ? "Save Changes" : "Save Draft")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SFAImportDialog({ open, onOpenChange, onCreated }) {
  const [regions, setRegions] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [regionId, setRegionId] = useState("");
  const [distId, setDistId] = useState("");
  const [file, setFile] = useState(null);
  const [rateType, setRateType] = useState("distributor");
  const [disc, setDisc] = useState(0); const [gst, setGst] = useState(18);
  const [terms, setTerms] = useState(""); const [remarks, setRemarks] = useState("");
  const [preview, setPreview] = useState(null); const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) return; api.get("/regions").then((r) => { setRegions(r.data); if (r.data[0]) setRegionId(r.data[0].id); }); api.get("/distributors").then((r) => setDistributors(r.data)); setFile(null); setPreview(null); }, [open]);
  const distInRegion = distributors.filter((d) => d.region_id === regionId);
  async function doPreview() {
    if (!file || !regionId) return toast.error("File + region required");
    const fd = new FormData(); fd.append("file", file); fd.append("region_id", regionId);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/imports/sfa-order/preview`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed"); setPreview(d);
    } catch (e) { toast.error(e.message); }
  }
  async function doApply() {
    if (!file || !distId || !regionId) return toast.error("Missing fields");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file); fd.append("region_id", regionId); fd.append("distributor_id", distId);
    fd.append("rate_type", rateType); fd.append("cash_disc_pct", String(disc)); fd.append("gst_pct", String(gst));
    fd.append("payment_terms", terms); fd.append("remarks", remarks);
    try {
      const token = localStorage.getItem("oms_token");
      const res = await fetch(`${API}/imports/sfa-order/apply`, { method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json(); if (!res.ok) throw new Error(d.detail || "Failed");
      toast.success(`PFI ${d.order.pfi_number} created from SFA`); onOpenChange(false); onCreated(d.order);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="heading text-xl">SFA Order → PFI</DialogTitle><p className="text-sm text-slate-500 mt-1">Upload an SFA xlsx. Matched SKUs are auto-applied with scheme engine + financials.</p></DialogHeader>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="md:col-span-2"><Label>XLSX File</Label><Input type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0])} /></div>
          <div><Label>Region</Label>
            <Select value={regionId} onValueChange={setRegionId}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Distributor</Label>
            <Select value={distId} onValueChange={setDistId}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{distInRegion.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label>Rate Type</Label>
            <Select value={rateType} onValueChange={setRateType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="distributor">Distributor</SelectItem><SelectItem value="super_stockist">Super Stockist</SelectItem></SelectContent></Select>
          </div>
          <div><Label>Credit Disc %</Label><Input type="number" value={disc} onChange={(e) => setDisc(e.target.value)} /></div>
          <div><Label>GST %</Label><Input type="number" value={gst} onChange={(e) => setGst(e.target.value)} /></div>
          <div className="md:col-span-2"><Label>Payment Terms</Label><Input value={terms} onChange={(e) => setTerms(e.target.value)} /></div>
          <div className="md:col-span-4"><Label>Remarks</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} /></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={doPreview}><FileSpreadsheet className="h-4 w-4 mr-1" /> Preview</Button>
          <Button onClick={doApply} disabled={busy || !distId || !file} className="bg-emerald-600 hover:bg-emerald-700 text-white"><CheckCircle2 className="h-4 w-4 mr-1" /> {busy ? "Creating…" : "Create Draft PFI"}</Button>
        </div>
        {preview && (
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="p-3 bg-slate-50 border-b text-sm">{preview.matched.length} matched · {preview.unmatched.length} unmatched {preview.party ? <>· Party: <b>{preview.party}</b></> : null}</div>
            <div className="max-h-56 overflow-auto text-xs">
              <table className="w-full">
                <tbody>
                  {preview.matched.map((it, i) => <tr key={i} className="border-b border-slate-100"><td className="p-2 mono">{it.sku}</td><td className="p-2">{it.name}</td><td className="p-2 text-right">{it.quantity}</td></tr>)}
                  {preview.unmatched.map((it, i) => <tr key={`u${i}`} className="bg-red-50 border-b border-red-100"><td className="p-2 mono">{it.sku}</td><td className="p-2 text-red-700">Unmatched</td><td className="p-2 text-right">{it.quantity}</td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Orders() {
  const { user, can, canAction } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(params.get("status") || "All");
  const [dateFrom, setDateFrom] = useState(params.get("date_from") || "");
  const [dateTo, setDateTo] = useState(params.get("date_to") || "");
  const [distPicker, setDistPicker] = useState(false);
  const [entry, setEntry] = useState(false);
  const [chosen, setChosen] = useState(null);
  const [editing, setEditing] = useState(null);
  const [sfa, setSfa] = useState(false);
  const [selected, setSelected] = useState(new Set());
  // Phase 4: bulk-delete + select-all driven by `delete_records` permission,
  // not hardcoded role. Legacy admin check retained as fallback.
  const canBulkDelete = canAction("delete_records") || user?.role === "admin" || user?.role === "super_admin";
  const isAdmin = canBulkDelete;

  async function load() {
    const p = { search, status };
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    const { data } = await api.get("/orders", { params: p });
    setOrders(data); setSelected(new Set());
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, status, dateFrom, dateTo]);
  useEffect(() => { const s = params.get("status"); if (s && s !== status) setStatus(s); /* eslint-disable-next-line */ }, [params]);
  useEffect(() => {
    const cur = params.get("status") || "All";
    const curFrom = params.get("date_from") || "";
    const curTo = params.get("date_to") || "";
    if (status !== cur || dateFrom !== curFrom || dateTo !== curTo) {
      const np = new URLSearchParams(params);
      if (status === "All") np.delete("status"); else np.set("status", status);
      if (dateFrom) np.set("date_from", dateFrom); else np.delete("date_from");
      if (dateTo) np.set("date_to", dateTo); else np.delete("date_to");
      setParams(np, { replace: true });
    }
    /* eslint-disable-next-line */
  }, [status, dateFrom, dateTo]);

  function toggle(id) { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); }
  function toggleAll() { setSelected(selected.size === orders.length ? new Set() : new Set(orders.map((o) => o.id))); }

  async function editDraft(o) {
    try {
      const { data: dist } = await api.get("/distributors");
      const d = dist.find((x) => x.id === o.distributor_id);
      if (!d) return toast.error("Distributor not found");
      setChosen(d); setEditing(o); setEntry(true);
    } catch { toast.error("Failed to load distributor"); }
  }

  async function bulkDelete() {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} order(s)? This permanently removes them and their activity history.`)) return;
    try {
      const { data } = await api.post("/orders/bulk-delete", { ids: Array.from(selected) });
      toast.success(`${data.deleted} order(s) deleted`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  }

  const PAYMENT_COLOR = {
    "Pending": "bg-slate-100 text-slate-700 border-slate-200",
    "Advance Received": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Credit Approved": "bg-blue-50 text-blue-700 border-blue-200",
    "Hold": "bg-amber-50 text-amber-700 border-amber-200",
    "Rejected": "bg-red-50 text-red-700 border-red-200",
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-[28px] font-bold text-slate-900 tracking-tight">Orders / PFI</h1>
          <p className="text-sm text-slate-500 mt-1">Create, track and manage proforma invoices through the full lifecycle.</p>
        </div>
        {can(["pfi", "operations"]) && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setSfa(true)} data-testid="open-sfa-import"><Upload className="h-4 w-4 mr-1.5" /> SFA Import</Button>
            <Button onClick={() => nav("/pfi/new")} data-testid="create-order-btn" className="bg-slate-900 hover:bg-slate-800 text-white"><Plus className="h-4 w-4 mr-1.5" /> New PFI</Button>
          </div>
        )}
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="flex-wrap h-auto bg-slate-100 p-1">
          <TabsTrigger value="All" data-testid="tab-all">All</TabsTrigger>
          {STATUS_ORDER.map((s) => <TabsTrigger key={s} value={s} data-testid={`tab-${s.toLowerCase().replace(/\s+/g, "-")}`}>{s}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PFI number, distributor, area…" className="pl-9" data-testid="orders-search" />
        </div>
        {isAdmin && selected.size > 0 && (
          <Button onClick={bulkDelete} variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid="orders-bulk-delete">
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete {selected.size} selected
          </Button>
        )}
      </div>

      <Card className="p-3 border border-slate-200 shadow-none rounded-xl">
        <DateRangeFilter
          from={dateFrom}
          to={dateTo}
          onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
          compact
          testidPrefix="orders-daterange"
        />
        {(dateFrom || dateTo) && (
          <div className="text-[11px] text-slate-500 mt-2" data-testid="orders-daterange-summary">
            Showing orders {dateFrom ? `from ${dateFrom}` : ""}{dateFrom && dateTo ? " " : ""}{dateTo ? `to ${dateTo}` : ""} · {orders.length} result{orders.length === 1 ? "" : "s"}
          </div>
        )}
      </Card>

      <Card className="border border-slate-200 shadow-none overflow-hidden rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-[11px] uppercase tracking-wide">
              <tr>
                {isAdmin && <th className="p-3.5 w-8"><Checkbox checked={orders.length > 0 && selected.size === orders.length} onCheckedChange={toggleAll} data-testid="orders-select-all" /></th>}
                <th className="p-3.5 text-left">PFI Number</th>
                <th className="p-3.5 text-left">Date</th>
                <th className="p-3.5 text-left">Area · Distributor</th>
                <th className="p-3.5 text-left">Region</th>
                <th className="p-3.5 text-right">Qty</th>
                <th className="p-3.5 text-right">Weight</th>
                <th className="p-3.5 text-right">Final</th>
                <th className="p-3.5 text-left">Status</th>
                <th className="p-3.5 text-left">Payment</th>
                <th className="p-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {orders.length === 0 ? <tr><td colSpan={isAdmin ? 11 : 10} className="p-12 text-center text-slate-500">No orders found.</td></tr> :
                orders.map((o) => (
                  <tr key={o.id} className="table-row-hover transition-colors" data-testid={`order-row-${o.pfi_number}`}>
                    {isAdmin && <td className="p-3.5" onClick={(e) => e.stopPropagation()}><Checkbox checked={selected.has(o.id)} onCheckedChange={() => toggle(o.id)} data-testid={`orders-sel-${o.pfi_number}`} /></td>}
                    <td onClick={() => nav(`/orders/${o.id}`)} className="p-3.5 mono text-[12px] font-semibold text-slate-900 cursor-pointer">{o.pfi_number}</td>
                    <td onClick={() => nav(`/orders/${o.id}`)} className="p-3.5 text-slate-700 cursor-pointer">{fmtDate(o.order_date)}</td>
                    <td onClick={() => nav(`/orders/${o.id}`)} className="p-3.5 cursor-pointer">
                      <div className="font-bold text-slate-900 text-[13.5px] leading-tight">{o.area || o.district || "—"}</div>
                      <div className="text-[11px] text-slate-500 truncate max-w-[220px]">{o.distributor_name}</div>
                    </td>
                    <td onClick={() => nav(`/orders/${o.id}`)} className="p-3.5 text-slate-600 text-xs cursor-pointer">{o.region_name || o.region}</td>
                    <td onClick={() => nav(`/orders/${o.id}`)} className="p-3.5 text-right text-slate-700 cursor-pointer">{o.total_quantity}</td>
                    <td onClick={() => nav(`/orders/${o.id}`)} className="p-3.5 text-right text-slate-700 cursor-pointer">{Number(o.total_weight || 0).toFixed(1)}</td>
                    <td onClick={() => nav(`/orders/${o.id}`)} className="p-3.5 text-right font-medium text-slate-900 cursor-pointer">{o.grand_total != null ? formatINR(o.grand_total) : <span className="text-slate-400">—</span>}</td>
                    <td onClick={() => nav(`/orders/${o.id}`)} className="p-3.5 cursor-pointer"><StatusBadge status={o.status} /></td>
                    <td onClick={() => nav(`/orders/${o.id}`)} className="p-3.5 cursor-pointer">{o.payment_status ? <span className={`text-[11px] px-2 py-0.5 rounded-full border ${PAYMENT_COLOR[o.payment_status] || PAYMENT_COLOR.Pending}`}>{o.payment_status}</span> : <span className="text-slate-400 text-xs">—</span>}</td>
                    <td className="p-3.5 text-right">
                      {o.status === "Draft" && can(["pfi"]) && (o.created_by_id === user.id || isAdmin) && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={(e) => { e.stopPropagation(); editDraft(o); }} data-testid={`edit-draft-${o.pfi_number}`}>Edit</Button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      <DistributorPicker open={distPicker} onOpenChange={setDistPicker} onPick={(d) => { setEditing(null); setChosen(d); setEntry(true); }} />
      <OrderEntryDialog open={entry} onOpenChange={(v) => { setEntry(v); if (!v) setEditing(null); }} distributor={chosen} order={editing} onCreated={(o) => { load(); if (!editing) nav(`/orders/${o.id}`); }} />
      <SFAImportDialog open={sfa} onOpenChange={setSfa} onCreated={(o) => { load(); nav(`/orders/${o.id}`); }} />
    </div>
  );
}
